import { Button, ButtonRow, Icon, PopoverConfirm, Tooltip, usePrevious } from '@timelyapp/react';
import TagFilterDropdown from 'components/dropdowns/TagFilterDropdown';
import FeatureGatedButton from 'components/FeatureGatedButton';
import MoveToArchivedWarningModal from 'components/modals/MoveToArchivedWarningModal';
import Popover from 'components/popovers/Popover';
import { HeaderRow } from 'components/ui-blocks/header';
import { useWithDispatch } from 'hooks/useWithDispatch';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getShowHourStateDropdown,
  getShowUpgradeModalForHourStateDropdown,
  getUserSelectableHourStates,
} from 'redux/modules/hourStates';
import { fetchProject, searchProjects } from 'redux/modules/projects.actions';
import { showSubscriptionNoticeModal } from 'redux/modules/subscriptions.actions';
import {
  addUsersToProjectThenUpdate,
  batchDeleteSelection,
  batchUpdateSelection,
} from 'redux/modules/superreports.actions';
import { batchSelectionActions } from 'redux/modules/superreports.types';
import { getReportBatchActionsData } from 'redux/selectors/superreports.batch';
import { getHourStateIcon } from 'routes/hourStates/HourStateIcon';
import HourStateDropdown from 'routes/supercalendar/components/calendar/HourStateDropdown';
import type { ProjectJoinMode } from 'types/hour';
import { dismissMoveToArchivedWarning, shouldShowMoveToArchivedWarning } from 'utils/localStorage';
import { hasLockedEntries } from '~redux/selectors/hours';
import type { IState } from '~redux/state';
import styles from './LoggedEntryBatchActions.module.scss';
import ReportBatchUpdateProject from './ReportBatchUpdateProject';

interface LoggedEntryBatchActionsProps {
  className?: string;
  reportsContext?: boolean;
  hasSelectedAllFiltered?: boolean;
  selectedRowsBillable?: boolean;
}

// When moving to a non-billable project, billable entries MUST be flipped to
// non-billable (the backend rejects billable entries in non-billable projects),
// so `updateBillable` is forced true regardless of the user's choice. When the
// target is billable it's the user's choice. Single source of truth shared by
// the billable-mismatch prompt, the combined prompt, and the required-tags
// prompt so the rule isn't re-derived (and drifted) in three places.
const resolveUpdateBillable = (
  selectedProjectBillableState: boolean | undefined,
  userChoseUpdate: boolean
): boolean => (selectedProjectBillableState ? userChoseUpdate : true);

// "1 user in your selection is …" / "N users in your selection are …" — shared
// by both membership prompts so the copy and pluralization live in one place.
const membershipPromptText = (count: number): string =>
  count === 1
    ? '1 user in your selection is not a member of this project.'
    : `${count} users in your selection are not a member of this project.`;

const RequiredTagsNote: React.FC = () => (
  <p className={styles.warningText}>
    Note: This project requires tags. Entries without required tags will not be updated.
  </p>
);

const LoggedEntryBatchActions: React.FC<LoggedEntryBatchActionsProps> = ({
  className,
  reportsContext,
  hasSelectedAllFiltered,
  selectedRowsBillable = true,
}) => {
  const { clients, currency, projects, tags, selection, states, ui, userId, selectedProject } =
    useSelector(getReportBatchActionsData);
  const showUpgradeModalForHourStateDropdown = useSelector(getShowUpgradeModalForHourStateDropdown);
  const showHourStateDropdown = useSelector(getShowHourStateDropdown);
  const userSelectableStates = useSelector(getUserSelectableHourStates);
  const hasLockedEntriesInSelection = useSelector((state: IState) =>
    hasLockedEntries(state, selection.hourIds)
  );

  const [showArchiveWarning, setShowArchiveWarning] = useState(false);
  const [pendingBillableUpdate, setPendingBillableUpdate] = useState<boolean | undefined>(
    undefined
  );
  const [pendingConvertPlannedTime, setPendingConvertPlannedTime] = useState<boolean | undefined>(
    undefined
  );
  const [pendingProjectJoinMode, setPendingProjectJoinMode] = useState<ProjectJoinMode | undefined>(
    undefined
  );

  const actions = useWithDispatch({
    setSelection: batchSelectionActions.setSelection,
    clearSelection: batchSelectionActions.clearSelection,
    batchUpdateSelection,
    batchDeleteSelection,
    addUsersToProjectThenUpdate,
    searchProjects,
    fetchProject,
    showSubscriptionNoticeModal,
  });

  const previousUi = usePrevious(ui);

  useEffect(() => {
    if (previousUi.canUpdateProject && !ui.canUpdateProject && selection.projectId) {
      actions.setSelection({ selectionKey: 'projectId', value: null });
    }

    if (previousUi.canUpdateTags && !ui.canUpdateTags && selection.labelIds.length) {
      actions.setSelection({ selectionKey: 'labelIds', value: [] });
    }
  }, [
    actions,
    previousUi.canUpdateProject,
    previousUi.canUpdateTags,
    selection.labelIds.length,
    selection.projectId,
    ui.canUpdateProject,
    ui.canUpdateTags,
  ]);

  const showUpgradeModal = useCallback(() => {
    actions.showSubscriptionNoticeModal('featureHourStatesRequiresUpgrade');
  }, [actions]);

  const handleCancel = () => {
    actions.clearSelection();
  };

  const handleTagBatchChange = (labelIds: Array<number>) => {
    actions.setSelection({
      selectionKey: 'labelIds',
      value: labelIds,
    });
  };

  const isMovingToArchivedProject = (): boolean => {
    if (!selection.projectId) return false;
    const targetProject = projects.find(p => p.base.id === selection.projectId);
    return !!targetProject && !targetProject.base.active;
  };

  const handleBatchUpdate = (
    updateBillable?: boolean,
    convertPlannedTimeToLogged?: boolean,
    projectJoinMode?: ProjectJoinMode
  ) => {
    const movingToArchived = isMovingToArchivedProject();

    if (ui.isUserAdmin && movingToArchived && shouldShowMoveToArchivedWarning()) {
      setPendingBillableUpdate(updateBillable);
      setPendingConvertPlannedTime(convertPlannedTimeToLogged);
      setPendingProjectJoinMode(projectJoinMode);
      setShowArchiveWarning(true);
      return;
    }

    // Clearing tags is a correctness requirement for moves to archived projects
    // (archived project tag settings can conflict with the selection's labels and
    // fail backend validation), not just a courtesy shown by the warning modal.
    // Apply it on every archived-project move, including when the warning was
    // dismissed via "Don't show this again".
    actions.batchUpdateSelection({
      reportsContext,
      updateBillable,
      convertPlannedTimeToLogged,
      hasSelectedAllFiltered,
      projectJoinMode,
      clearTags: movingToArchived,
    });
  };

  const handleAddUsersToProject = (updateBillable = false) => {
    if (!selection.projectId || !selectedProject) return;
    actions.addUsersToProjectThenUpdate({
      projectId: selection.projectId,
      userIds: ui.usersNotInProject,
      project: selectedProject,
      reportsContext,
      hasSelectedAllFiltered,
      updateBillable,
    });
  };

  const handleSkipUsersNotInProject = (updateBillable = false) => {
    actions.batchUpdateSelection({
      reportsContext,
      updateBillable,
      convertPlannedTimeToLogged: false,
      hasSelectedAllFiltered,
      skipUserIds: ui.usersNotInProject,
    });
  };

  // ALL_PROJECTS_LOGGING: the author of the selected entries isn't a member of
  // the target project. Mirror the single-entry dialog's choices for bulk.
  const handleProjectJoin = (projectJoinMode: ProjectJoinMode) => {
    handleBatchUpdate(false, false, projectJoinMode);
  };

  const handleArchiveWarningConfirm = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      dismissMoveToArchivedWarning();
    }
    setShowArchiveWarning(false);
    actions.batchUpdateSelection({
      reportsContext,
      updateBillable: pendingBillableUpdate,
      convertPlannedTimeToLogged: pendingConvertPlannedTime,
      hasSelectedAllFiltered,
      projectJoinMode: pendingProjectJoinMode,
      clearTags: true, // Clear tags to avoid validation conflicts with archived project's tag settings
    });
    setPendingBillableUpdate(undefined);
    setPendingConvertPlannedTime(undefined);
    setPendingProjectJoinMode(undefined);
  };

  const handleArchiveWarningCancel = () => {
    setShowArchiveWarning(false);
    setPendingBillableUpdate(undefined);
    setPendingConvertPlannedTime(undefined);
    setPendingProjectJoinMode(undefined);
  };

  const handleStateChange = useCallback(
    (stateIds: Array<number> | null) => {
      actions.setSelection({
        selectionKey: 'stateId',
        value: stateIds?.[0]!,
      });
    },
    [actions]
  );

  if (!ui.isVisible) {
    return null;
  }

  const PrimaryButton = ({ onClick }: { onClick?: any }) => {
    const button = (
      <Button
        data-testid="update_hour_state"
        priority="xhigh"
        disabled={ui.updateActionDisabled}
        size="small"
        caret={false}
      >
        {ui.updateLabel}
      </Button>
    );
    const selectedState =
      selection.stateId !== undefined ? userSelectableStates[selection.stateId] : undefined;
    const showConfirmMessage =
      selectedState &&
      !selectedState.locked &&
      hasLockedEntriesInSelection &&
      selection.hourIds.length > 1;
    return showConfirmMessage ? (
      <PopoverConfirm
        onConfirm={e => onClick?.(e)}
        prompt="Are you sure you want to unlock selected entries?"
        target={button}
        confirmLabel="Proceed"
      />
    ) : (
      React.cloneElement(button, { onClick: (e: React.SyntheticEvent) => onClick?.(e) })
    );
  };
  const selectionCounter = (
    <div className={styles.text}>
      <div data-testid="number_of_entries" className={styles.label}>
        <strong>{ui.affectedEntriesLabel}</strong>selected
      </div>
    </div>
  );

  if (ui.allHoursAreReadonly) {
    return (
      <HeaderRow className={className}>
        <ButtonRow>
          {selectionCounter}

          <div className={styles.disabledReason} data-testid="bulk-editing-locked-reaason">
            The selected entries are locked for editing.
          </div>
        </ButtonRow>
      </HeaderRow>
    );
  }

  const renderHourStateDropdown = () => {
    if (showUpgradeModalForHourStateDropdown) {
      return (
        <FeatureGatedButton size="small" isFeatureLocked onClickLocked={showUpgradeModal} caret>
          Update state
        </FeatureGatedButton>
      );
    }
    if (showHourStateDropdown) {
      const hasStateSelected = selection.stateId !== undefined;
      const isNoStateSelected = selection.stateId === 0;

      return (
        <Tooltip tooltip={ui.cannotUpdateStateReason}>
          <HourStateDropdown
            onChange={handleStateChange}
            disableBillable={!selectedRowsBillable}
            filterMode={false}
            showNoState
          >
            <Button
              priority="medium"
              size="small"
              data-testid="report_batch_update_state"
              disabled={!ui.canUpdateState}
              caret
              selected={hasStateSelected}
            >
              {isNoStateSelected ? (
                <>
                  <Icon name="Contrast01" type="solid" />
                  No state
                </>
              ) : hasStateSelected ? (
                <>
                  {getHourStateIcon(states[selection.stateId!])}
                  {states[selection.stateId!].name}
                </>
              ) : (
                <>
                  <Icon name="Contrast01" type="solid" />
                  Update state
                </>
              )}
            </Button>
          </HourStateDropdown>
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <>
      <HeaderRow className={className}>
        <ButtonRow>
          {selectionCounter}
          {renderHourStateDropdown()}
          <Tooltip tooltip={ui.cannotUpdateTagsReason}>
            <TagFilterDropdown
              disableSelectNone
              highlightModified={true}
              emptyLabel="Update tags"
              batchMode
              selected={selection.labelIds}
              tags={tags}
              onChange={handleTagBatchChange}
              singleChildSelect
              clearTooltip="Clear the tags"
              disabled={!ui.canUpdateTags}
              size="small"
            />
          </Tooltip>
          <Tooltip tooltip={ui.cannotUpdateProjectReason}>
            <ReportBatchUpdateProject
              allProjectsLoaded={ui.allProjectsLoaded}
              clients={clients}
              currency={currency}
              isAdmin={ui.isUserAdmin}
              isLoading={ui.isLoadingProjects}
              projectId={selection.projectId}
              projects={projects}
              searchProjects={actions.searchProjects}
              setSelection={actions.setSelection}
              userId={userId}
              fetchProject={actions.fetchProject}
              disabled={!ui.canUpdateProject}
              size="small"
            />
          </Tooltip>
          {ui.showBatchLogPlannedTime && (
            <PopoverConfirm
              targetCaret={false}
              target={
                <Button priority="low" style={{ marginLeft: 5 }} size="small">
                  Log planned time
                </Button>
              }
              confirmButtonPriority="high"
              prompt="This will convert all planned time into logged time for the selected entries."
              onConfirm={() => handleBatchUpdate(undefined, true)}
            />
          )}
          {ui.userIsNotInProject && !ui.isLoadingProjects && ui.hasProjectBillableMismatch ? (
            // Both conditions apply: users not in project AND billable mismatch.
            // Show a single popover that asks both questions using existing copy/options.
            <CombinedMembershipBillablePopover
              usersNotInProjectCount={ui.usersNotInProject.length}
              selectedProjectBillableState={!!ui.selectedProjectBillableState}
              selectedProjectHasRequiredTags={ui.selectedProjectHasRequiredTags}
              onAddUsersToProject={handleAddUsersToProject}
              onSkipUsersNotInProject={handleSkipUsersNotInProject}
              target={<PrimaryButton />}
            />
          ) : ui.userIsNotInProject && !ui.isLoadingProjects ? (
            // Some users from the selection are not members of the target project.
            // Prompt user to either add them to the project or skip their entries.
            <Popover stopPropagation closeOnClick target={<PrimaryButton />}>
              <div className={styles.usersNotInProjectPrompt}>
                <p>{membershipPromptText(ui.usersNotInProject.length)}</p>
                <p data-testid="users_not_in_project_prompt">
                  Would you like to add them to the project, or skip their time entries?
                </p>
                {ui.selectedProjectHasRequiredTags && <RequiredTagsNote />}
                <div className={styles.buttons}>
                  <Button size="small" priority="xhigh" onClick={() => handleAddUsersToProject()}>
                    Add to project
                  </Button>
                  <div className={styles.secondaryActions}>
                    <Button size="small" onClick={() => handleSkipUsersNotInProject()}>
                      Skip entries
                    </Button>
                    <Button size="small" priority="low">
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </Popover>
          ) : ui.promptProjectJoin && !ui.isLoadingProjects ? (
            // ALL_PROJECTS_LOGGING: the author isn't a member of the target
            // project. Ask how to log — same choices/wording as the single-
            // entry hour-editor dialog. No "Cancel" here: dismiss via the
            // action-bar Cancel or by clicking outside the popover.
            <Popover stopPropagation closeOnClick target={<PrimaryButton />}>
              <div className={styles.updateBillablePrompt}>
                <p>
                  The author of your selected {selection.hourIds.length === 1 ? 'entry' : 'entries'}{' '}
                  isn't a member of {ui.selectedProjectName ?? 'this project'} yet.
                </p>
                <p data-testid="project_join_prompt">How do you want to log this time?</p>
                <div className={styles.projectJoinButtons}>
                  <Button size="small" priority="xhigh" onClick={() => handleProjectJoin('add')}>
                    Join project &amp; log time
                  </Button>
                  <Button size="small" onClick={() => handleProjectJoin('log_once')}>
                    Log time only this once
                  </Button>
                </div>
              </div>
            </Popover>
          ) : ui.selectedProjectHasRequiredTags ? (
            // Target project has required tags - warn user that some entries may fail.
            // Still apply the billable correction: if there's also a billable
            // mismatch (esp. a non-billable target), entries must be flipped, so
            // we can't just send updateBillable=false here.
            <PopoverConfirm
              onConfirm={() =>
                handleBatchUpdate(
                  ui.hasProjectBillableMismatch
                    ? resolveUpdateBillable(ui.selectedProjectBillableState, false)
                    : false
                )
              }
              prompt={
                <>
                  This project requires tags. Entries without the required tags will not be updated.
                </>
              }
              target={<PrimaryButton />}
              confirmLabel="Proceed"
            />
          ) : ui.hasProjectBillableMismatch ? (
            // - If ui.selectedProjectBillableState is true, we are moving from a non-billable
            //   project to a billable project, so we give the user the option to update the
            //   billable state of the hours.
            // - If ui.selectedProjectBillableState is false, we are moving from a billable to a
            //   non-billable project. We don't support billable entries in non-billable projects
            //   yet, so these entries must be made non-billable.
            <Popover stopPropagation closeOnClick target={<PrimaryButton />}>
              <div className={styles.updateBillablePrompt}>
                <p>
                  The project you've indicated is{' '}
                  {ui.selectedProjectBillableState ? 'billable' : 'non-billable'}, but some of the
                  entries are {!ui.selectedProjectBillableState ? 'billable' : 'non-billable'}.
                </p>
                <p data-testid="billable_prompt">
                  {ui.selectedProjectBillableState
                    ? 'Would you like to mark these entries as billable?'
                    : 'These entries will be automatically marked as non-billable.'}
                </p>
                <div className={styles.buttons}>
                  <Button size="small" priority="xhigh" onClick={() => handleBatchUpdate(true)}>
                    {ui.selectedProjectBillableState ? 'Yes' : 'OK'}
                  </Button>
                  {ui.selectedProjectBillableState && (
                    <Button size="small" onClick={() => handleBatchUpdate(false)}>
                      No
                    </Button>
                  )}
                  <Button size="small">Cancel</Button>
                </div>
              </div>
            </Popover>
          ) : (
            <PrimaryButton onClick={() => handleBatchUpdate(false)} />
          )}
          <Tooltip tooltip={ui.cannotDeleteReason}>
            <PopoverConfirm
              onConfirm={actions.batchDeleteSelection}
              prompt={ui.deleteConfirmMessage}
              target={
                <Button
                  data-testid="delete_entries"
                  caret={false}
                  priority="high"
                  disabled={ui.deleteActionDisabled}
                  destructive
                  size="small"
                >
                  {ui.deleteLabel}
                </Button>
              }
              confirmLabel="Proceed"
            />
          </Tooltip>
          <Button priority="low" onClick={handleCancel} size="small">
            Cancel
          </Button>
        </ButtonRow>
      </HeaderRow>
      {ui.hasReadonlyHours && !ui.allHoursAreReadonly && (
        <HeaderRow>
          <div data-testid="warning_msg" className={styles.readonlyWarning}>
            Some of the selected entries are locked for editing and won't be updated
          </div>
        </HeaderRow>
      )}
      <MoveToArchivedWarningModal
        isOpen={showArchiveWarning}
        onConfirm={handleArchiveWarningConfirm}
        onCancel={handleArchiveWarningCancel}
      />
    </>
  );
};

interface CombinedPopoverProps {
  usersNotInProjectCount: number;
  selectedProjectBillableState: boolean;
  selectedProjectHasRequiredTags: boolean;
  onAddUsersToProject: (updateBillable: boolean) => void;
  onSkipUsersNotInProject: (updateBillable: boolean) => void;
  target: React.ReactElement;
}

const CombinedMembershipBillablePopover: React.FC<CombinedPopoverProps> = ({
  usersNotInProjectCount,
  selectedProjectBillableState,
  selectedProjectHasRequiredTags,
  onAddUsersToProject,
  onSkipUsersNotInProject,
  target,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [membershipChoice, setMembershipChoice] = useState<'add' | 'skip'>('add');
  // Default billable choice aligns entries with the target project's billable state,
  // matching the default action offered by the standalone billable-mismatch prompt.
  const [billableChoice, setBillableChoice] = useState<'update' | 'keep'>('update');

  const resetChoices = () => {
    setMembershipChoice('add');
    setBillableChoice('update');
  };

  const handleApply = () => {
    const updateBillable = resolveUpdateBillable(
      selectedProjectBillableState,
      billableChoice === 'update'
    );
    setIsOpen(false);
    resetChoices();
    if (membershipChoice === 'add') {
      onAddUsersToProject(updateBillable);
    } else {
      onSkipUsersNotInProject(updateBillable);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    resetChoices();
  };

  return (
    <Popover
      stopPropagation
      closeOnClick={false}
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => {
        setIsOpen(false);
        resetChoices();
      }}
      target={target}
    >
      <div className={styles.combinedPrompt} data-testid="combined_membership_billable_prompt">
        <section>
          <p>{membershipPromptText(usersNotInProjectCount)}</p>
          <p>Would you like to add them to the project, or skip their time entries?</p>
          {selectedProjectHasRequiredTags && <RequiredTagsNote />}
          <div className={styles.choiceRow}>
            <Button
              size="small"
              priority={membershipChoice === 'add' ? 'xhigh' : 'medium'}
              onClick={() => setMembershipChoice('add')}
            >
              Add to project
            </Button>
            <Button
              size="small"
              priority={membershipChoice === 'skip' ? 'xhigh' : 'medium'}
              onClick={() => setMembershipChoice('skip')}
            >
              Skip entries
            </Button>
          </div>
        </section>
        <section>
          <p>
            The project you've indicated is{' '}
            {selectedProjectBillableState ? 'billable' : 'non-billable'}, but some of the entries
            are {!selectedProjectBillableState ? 'billable' : 'non-billable'}.
          </p>
          <p>
            {selectedProjectBillableState
              ? 'Would you like to mark these entries as billable?'
              : 'These entries will be automatically marked as non-billable.'}
          </p>
          {selectedProjectBillableState && (
            <div className={styles.choiceRow}>
              <Button
                size="small"
                priority={billableChoice === 'update' ? 'xhigh' : 'medium'}
                onClick={() => setBillableChoice('update')}
              >
                Yes
              </Button>
              <Button
                size="small"
                priority={billableChoice === 'keep' ? 'xhigh' : 'medium'}
                onClick={() => setBillableChoice('keep')}
              >
                No
              </Button>
            </div>
          )}
        </section>
        <div className={styles.buttons}>
          <Button size="small" priority="xhigh" onClick={handleApply}>
            Apply
          </Button>
          <Button size="small" priority="low" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </Popover>
  );
};

export default LoggedEntryBatchActions;
